'use client'

import { getTenantPublic } from '@/services/tenant/get-tenant'
import { TenantPublicResponse } from '@/types/tenant'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

/**
 * Takes the tenant as an argument rather than reading TenantProvider: the header lives
 * in the layout, outside any page, so it resolves the tenant from the URL itself.
 */
export default function useGetTenant(tenantId: string | null) {
  const query = useQuery({
    queryKey: ['tenant-public', tenantId],
    queryFn: async () => {
      const response = await getTenantPublic(tenantId as string)
      return response.data
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    // The endpoint is absent in some environments (404 on production today). Retrying a
    // 4xx just multiplies a request that will never succeed — the name falls back to
    // NEXT_PUBLIC_SHOP_SITE_NAME either way.
    retry: (failureCount, error) => {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      if (status && status >= 400 && status < 500) return false
      return failureCount < 2
    },
  })

  const tenant = query.data as TenantPublicResponse | undefined

  return {
    tenant,
    name: tenant?.name ?? null,
    frontendUrl: tenant?.frontendUrl ?? null,
    isLoading: query.isLoading,
  }
}
